import clsx from 'clsx'
import React, { FC, useEffect } from 'react'
import { cloneDeep } from 'lodash'
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form/dist/types/form'

import Input from '../../../../common/inputs/Input'
import { JumpOffToggle } from '../JumpOffToggle/JumpOffToggle'

import {
  IScoreCardInterface,
  IScoreCardSection,
} from '../../../../../models/score-cards/score-cards.interface'

import { ROUND_OPTIONS_PROPS } from '../../../../../const/score-card-const'

function calculateTimeFaults(actualTime: number, timeLimit: number): number {
  return actualTime <= timeLimit ? 0 : Math.ceil(actualTime - timeLimit)
}

interface ScoringModalSectionProps {
  section: IScoreCardSection
  sectionIndex: number
  setValue: UseFormSetValue<IScoreCardInterface>
  watch: UseFormWatch<IScoreCardInterface>
  register: UseFormRegister<IScoreCardInterface>
  editable: boolean
  isLocked: boolean
  showLockedModal: () => void
}

const LabelBlock: FC<{ text: string; highlighted?: boolean }> = ({ text, highlighted }) => (
  <div
    className={clsx(
      'w-[148px] rounded-md font-bold text-base h-[56px] p-2 flex items-center justify-center',
      highlighted ? 'bg-[#F7074F]/5 text-[#F7074F]' : 'bg-SeabiscuitGrayThemeColor'
    )}
  >
    {text}
  </div>
)

const ValueBlock: FC<{ children: React.ReactNode; highlighted?: boolean }> = ({
  children,
  highlighted,
}) => (
  <div
    className={clsx(
      'flex-1 rounded-md font-bold text-base placeholder:font-normal h-[56px] p-2 flex items-center justify-center border',
      highlighted
        ? 'bg-[#F7074F]/5 border-transparent text-[#F7074F]'
        : 'bg-transparent border-SeabiscuitLightThemeColorD3'
    )}
  >
    {children}
  </div>
)

export const CustomScoringModalSection: FC<ScoringModalSectionProps> = ({
  section,
  sectionIndex,
  setValue,
  watch,
  register,
  editable,
  isLocked,
  showLockedModal,
}) => {
  useEffect(() => {
    const totalTime = Number(section.totalTime ?? 0)
    const maxTime = Number(section.maxTime ?? 0)
    const timeFaults = calculateTimeFaults(totalTime, maxTime)
    setValue(`sections.${sectionIndex}.totalTimeFaults`, timeFaults)
  }, [section.maxTime, section.totalTime, sectionIndex])

  const onClickOption = (rowIndex: number, optionIndex: number) => {
    const updatedRows = cloneDeep(section.rows)
    const currentOption = updatedRows[rowIndex].options[optionIndex]

    currentOption.isActive = !currentOption.isActive

    let faults = 0
    let displaced = 0
    let refusal = 0
    let statusParts: string[] = []

    updatedRows.forEach((row) => {
      row.options.forEach((opt) => {
        if (!opt.isActive) return
        switch (opt.name) {
          case 'Fault':
            faults += 1
            break
          case 'D':
            displaced += 1
            break
          case 'R1':
          case 'R2':
            refusal += 1
            break
          case 'RT':
            statusParts.push('Retired')
            break
          case 'F':
            statusParts.push('Fall')
            break
        }
      })
    })

    const totalFaultsCount = (faults + displaced + (refusal >= 2 ? 0 : refusal)) * 4
    if (refusal >= 2) statusParts.push('Refusal')

    const totalFaultsStatus =
      statusParts.length > 0 ? `Eliminated • ${statusParts.join(' • ')}` : ''

    setValue(
      `sections.${sectionIndex}.rows.${rowIndex}.options.${optionIndex}.isActive`,
      currentOption.isActive
    )
    setValue(`sections.${sectionIndex}.totalFaultsStatus`, totalFaultsStatus)
    setValue(`sections.${sectionIndex}.totalFaultsCount`, totalFaultsCount)
  }

  const showSection = section.type !== 'jump off' || watch('showJumpOff')

  if (!showSection) return null

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mt-4 pt-4 border-t bordert-SeabiscuitLightThemeColorD3">
        <div className="flex gap-4">
          <h4 className="text-[20px]">{section.name}</h4>
          {section.type === 'jump off' && (
            <JumpOffToggle hideTitle setValue={setValue} watch={watch} editable={editable} />
          )}
        </div>
        <div className="ml-auto bg-SeabiscuitGrayThemeColor py-2 px-4 rounded-full">
          Max time: {section.maxTime ?? 0} seconds
        </div>
      </div>
      <div className="overflow-auto">
        <div className="flex flex-col gap-2.5 mt-8 min-w-[640px]">
          {section.rows?.map((row, rowIndex) => (
            <div key={row.id} className="flex gap-2.5 justify-between">
              <LabelBlock text={row.description} />
              {row.options?.map((option, optionIndex) => (
                <button
                  key={option.name}
                  className={clsx(
                    'relative rounded-md text-base h-[56px] p-2 flex items-center justify-center border border-solid',
                    ROUND_OPTIONS_PROPS[option.name].className,
                    option.isActive
                      ? 'border-[#F7074F]/5 bg-[#F7074F]/5 text-[#F7074F]'
                      : 'border-SeabiscuitLightThemeColorD3 text-SeabiscuitDark200ThemeColor/50',
                    editable
                      ? 'hover:border-[#F7074F]/5 hover:text-[#F7074F] hover:bg-[#F7074F]/5'
                      : 'cursor-default'
                  )}
                  onClick={() => {
                    if (editable) {
                      if (isLocked) showLockedModal()
                      else onClickOption(rowIndex, optionIndex)
                    }
                  }}
                >
                  {option.name}
                  {rowIndex === 0 && (
                    <div className="text-left text-SeabiscuitDark200ThemeColor/50 text-xs absolute left-0 w-full top-0 -translate-y-full">
                      {ROUND_OPTIONS_PROPS[option.name].label}
                    </div>
                  )}
                </button>
              ))}
            </div>
          ))}

          <div className="w-full flex gap-2.5 justify-between">
            <LabelBlock
              text="Total Faults"
              highlighted={!!section.totalFaultsStatus || section.totalFaultsCount > 0}
            />
            <ValueBlock highlighted={!!section.totalFaultsStatus || section.totalFaultsCount > 0}>
              {section.totalFaultsStatus || section.totalFaultsCount}
            </ValueBlock>
          </div>

          <div className="w-full flex gap-2.5 justify-between">
            <LabelBlock text="Time" highlighted={section.totalTime > 0} />
            <ValueBlock highlighted={section.totalTime > 0}>
              <Input
                className={clsx(
                  'p-0 border-none bg-transparent text-center !text-base placeholder:!text-base',
                  section.totalTime > 0 && 'text-[#F7074F]'
                )}
                disabled={!editable}
                type="text"
                name={`sections.${sectionIndex}.totalTime`}
                placeholder="0"
                register={register}
                value={watch(`sections.${sectionIndex}.totalTime`)}
                onChange={(event) => {
                  if (isLocked) {
                    showLockedModal()
                  } else {
                    let value = event.target.value
                    // Replace comma with dot
                    value = value.replace(',', '.')

                    // Remove leading zeros
                    value = value.replace(/^0+(?=\d)/, '')

                    // Allow only digits and one dot
                    value = value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')

                    setValue(`sections.${sectionIndex}.totalTime`, value)
                  }
                }}
              />
            </ValueBlock>
          </div>

          <div className="w-full flex gap-2.5 justify-between">
            <LabelBlock text="Time Faults" highlighted={section.totalTime > 0} />
            <ValueBlock highlighted={section.totalTime > 0}>{section.totalTimeFaults}</ValueBlock>
          </div>
        </div>
      </div>
    </div>
  )
}
